import {
	Button,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Modal,
	RichText
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";

import * as styles from "./MobileMenu.module.scss";

interface MenuContainerSection<L, I> {
	type: "container";
	subsections: MenuSection<L, I>[];
}

interface MenuTextSection {
	type: "text";
	heading: HeadingProps;
	content?: string;
}

interface MenuLinkSection<LinkComponentProps> {
	type: "link";
	heading?: HeadingProps;
	items: LinkProps<LinkComponentProps>[];
}

interface MenuCategorySection<LinkComponentProps, ImageComponentProps> {
	type: "category";
	heading?: HeadingProps;
	items: {
		image: ImageProps<ImageComponentProps>;
		link: LinkProps<LinkComponentProps>;
	}[];
}

type MenuSection<L, I> =
	| MenuContainerSection<L, I>
	| MenuTextSection
	| MenuLinkSection<L>
	| MenuCategorySection<L, I>;

export interface MobileMenuProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	label: string;
	sections: MenuSection<LinkComponentProps, ImageComponentProps>[];
	onMyShowsClick?: () => void;
}

/**
 * Header mobile menu component
 */
export function MobileMenu<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	label,
	sections,
	onMyShowsClick
}: MobileMenuProps<LinkComponentProps, ImageComponentProps>): JSX.Element {
	/**
	 * Renders menu section
	 * @param section Section to be rendered
	 * @param key
	 * @returns Rendered nodes
	 */
	function renderMenuSection(
		section: MenuSection<LinkComponentProps, ImageComponentProps>,
		key: number
	): JSX.Element {
		return (
			<div
				key={key}
				{...classNameHelper(
					styles["menu-section"],
					styles[section.type]
				)}
			>
				{section.type === "container" ? (
					section.subsections.map(renderMenuSection)
				) : (
					<>
						{section.heading && (
							<Heading
								{...section.heading}
								{...classNameHelper(
									styles["section-heading"],
									section.heading.className
								)}
							/>
						)}
						{section.type === "text" && section.content ? (
							<RichText
								className={styles["section-text"]}
								content={section.content}
							/>
						) : undefined}
						{section.type === "link"
							? section.items.map((linkItem, idx) => {
									const isMyShows =
										onMyShowsClick &&
										typeof linkItem.children === "string" &&
										linkItem.children
											.toLowerCase()
											.includes("my shows");

									return (
										<Link<LinkComponentProps>
											key={idx}
											{...linkItem}
											href={
												isMyShows
													? undefined
													: linkItem.href
											}
											onPress={
												isMyShows
													? () => {
															onMyShowsClick();
														}
													: linkItem.onPress
											}
											onClick={
												isMyShows
													? (e) => {
															e.preventDefault();
															onMyShowsClick();
														}
													: linkItem.onClick
											}
											kind="sml"
											className={styles["menu-link"]}
										/>
									);
								})
							: undefined}
						{section.type === "category" ? (
							<div className={styles["category-container"]}>
								{section.items.map((categoryItem, idx) => (
									<Link<LinkComponentProps>
										key={idx}
										{...categoryItem.link}
										kind="xxsl"
										className={styles["category-link"]}
										textClassName={
											styles["category-link-content"]
										}
									>
										<Image
											{...categoryItem.image}
											{...classNameHelper(
												styles["category-image"],
												categoryItem.image.className
											)}
											alt={
												categoryItem.image.alt ||
												"Category Item Image"
											}
										/>
										{categoryItem.link.children}
									</Link>
								))}
							</div>
						) : undefined}
					</>
				)}
			</div>
		);
	}

	return (
		<Modal
			triggerElement={
				<Button
					{...classNameHelper(styles["trigger-button"])}
					kind="tertiary"
					size="sm"
					icon={{ name: "ChevronDown", position: "right" }}
				>
					{label}
				</Button>
			}
			className={styles["mobile-menu"]}
			closeButtonClassName={styles["close-button"]}
		>
			{sections.map(renderMenuSection)}
		</Modal>
	);
}
